import { useMemo, useRef, useState, useEffect } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import { toast } from "sonner";
import { api, apiError, API } from "@/lib/apiClient";
import { Brand } from "@/components/Brand";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import {
  ShieldCheck, Users, HandHeart, Landmark, HeartPulse, Flame, Scale, Umbrella,
  ArrowRight, CheckCircle2, Loader2, Info, Mail, Copy, MessageCircle, FileText, Phone, Sparkles, Lightbulb, ShieldQuestion, Headphones, FileDown,
} from "lucide-react";
import { PRODUCT_CONTENT, LANDING_SLUGS } from "@/data/productContent";
import { ComplianceNotice } from "@/components/ComplianceNotice";
import { PrivacyConsent, LegalFooterLinks, AiContentBadge } from "@/components/LegalConsent";

const ICONS = { ShieldCheck, Users, HandHeart, Landmark, HeartPulse, Flame, Scale, Umbrella };
const FALLBACK_IMG = "https://images.unsplash.com/photo-1758599668299-beebedfabf7b?auto=format&fit=crop&w=1600&q=70";

const TIPI = [
  { value: "informativa", label: "Richiesta informativa" },
  { value: "consulenza", label: "Richiesta di consulenza" },
  { value: "contatto", label: "Richiesta di contatto" },
];

const COME_FUNZIONA = [
  { icon: Mail, title: "1 · Lascia i tuoi dati", text: "Compila il form: bastano pochi dati per farci capire le tue esigenze." },
  { icon: FileText, title: "2 · Ricevi la documentazione", text: "Ti inviamo via email la documentazione informativa e precontrattuale del prodotto." },
  { icon: Phone, title: "3 · Consulenza dedicata", text: "Un nostro incaricato ti ricontatta per una proposta su misura, senza impegno." },
];

export default function ProductLanding() {
  const { slug } = useParams();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get("ref") || "";
  const code = LANDING_SLUGS[slug];
  const product = useMemo(() => PRODUCT_CONTENT[code], [code]);
  const formRef = useRef(null);
  const [podcastOn, setPodcastOn] = useState(false);
  useEffect(() => {
    if (!code) return;
    fetch(`${API}/public/products/${code}/podcast-cliente`)
      .then((r) => r.json()).then((d) => setPodcastOn(!!d.available)).catch(() => {});
  }, [code]);
  const [form, setForm] = useState({ nome: "", ente: "", email: "", telefono: "", tipo_richiesta: "informativa", note: "" });
  const [privacyOk, setPrivacyOk] = useState(false);
  const [sent, setSent] = useState(false);
  const [sentMsg, setSentMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  if (!product || !product.landing) {
    return (
      <div className="flex min-h-screen flex-col items-center justify-center gap-4 bg-bone text-center">
        <p className="text-forest">Prodotto non trovato.</p>
        <Link to="/login" className="text-terracotta underline">Torna al portale</Link>
      </div>
    );
  }

  const L = product.landing;
  const heroImg = L.heroImg || FALLBACK_IMG;
  const PUBLIC_URL = typeof window !== "undefined" ? window.location.href : "";
  const SHARE_TEXT = `Scopri ${product.name}: la soluzione assicurativa dedicata al Terzo Settore. Richiedi informazioni gratuite:`;
  const WHATSAPP_URL = `https://wa.me/?text=${encodeURIComponent(`${SHARE_TEXT} ${PUBLIC_URL}`)}`;

  const scrollToForm = () => formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(PUBLIC_URL); toast.success("Link copiato — incollalo dove vuoi"); }
    catch { toast.error("Copia non riuscita, copia manualmente il link"); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!form.nome.trim()) { setError("Inserisci il tuo nome."); return; }
    if (!form.email.trim()) { setError("Inserisci l'email: ti invieremo la documentazione informativa."); return; }
    if (!privacyOk) { setError("Per procedere devi accettare l'informativa privacy."); return; }
    setLoading(true);
    try {
      const { data } = await api.post("/public/info-lead", { ...form, product_code: product.code, codice_collab: refCode });
      setSent(true);
      setSentMsg(data.message || "");
      toast.success(data.message || "Richiesta inviata.");
      formRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    } catch (err) {
      setError(apiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-bone" data-testid={`landing-${product.slug}`}>
      <header className="sticky top-0 z-30 border-b border-border/60 bg-bone/85 backdrop-blur-md">
        <div className="mx-auto flex max-w-5xl items-center justify-between px-4 py-3 md:px-6">
          <Brand />
          <div className="flex items-center gap-2">
            <Button size="sm" variant="outline" onClick={copyLink} data-testid="landing-copy-link" className="hidden gap-1.5 border-forest/30 text-forest hover:bg-sage sm:inline-flex">
              <Copy className="h-4 w-4" /> Copia link
            </Button>
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" data-testid="landing-whatsapp"
              className="hidden items-center gap-1.5 rounded-md border border-forest/30 px-3 py-1.5 text-sm font-medium text-forest transition-colors hover:bg-sage sm:inline-flex">
              <MessageCircle className="h-4 w-4" /> WhatsApp
            </a>
            <Button onClick={scrollToForm} size="sm" className="gap-1.5 rounded-full bg-terracotta hover:bg-terracotta/90" data-testid="hero-cta-top">
              Richiedi informazioni <ArrowRight className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </header>

      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img src={heroImg} alt="" className="h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-forest via-forest/92 to-forest/55" />
        </div>
        <div className="relative mx-auto max-w-5xl px-4 py-16 md:px-6 md:py-24">
          <span className="inline-flex items-center gap-1.5 rounded-full bg-white/15 px-3 py-1 text-xs font-medium uppercase tracking-wide text-white/90 backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" /> {L.eyebrow}
          </span>
          <h1 className="mt-4 max-w-3xl font-heading text-4xl font-semibold leading-tight text-white sm:text-5xl">
            {L.title}
          </h1>
          <p className="mt-4 max-w-2xl text-base text-white/85 md:text-lg">{L.lead}</p>
          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Button onClick={scrollToForm} size="lg" className="gap-2 rounded-full bg-terracotta hover:bg-terracotta/90" data-testid="hero-cta">
              Richiedi informazioni gratuite <ArrowRight className="h-5 w-5" />
            </Button>
            <a href={`${API}/public/products/${code}/brochure`} target="_blank" rel="noopener noreferrer" data-testid="landing-brochure-download"
              className="inline-flex items-center gap-2 rounded-full border border-white/40 px-5 py-2.5 text-sm font-medium text-white transition-colors hover:bg-white/10">
              <FileDown className="h-4 w-4" /> Scarica la brochure
            </a>
            <span className="text-sm text-white/70">Servizio informativo, senza impegno.</span>
          </div>
        </div>
      </section>

      {/* STATS STRIP */}
      {L.stats?.length > 0 && (
        <section className="border-b border-border/60 bg-forest">
          <div className="mx-auto grid max-w-5xl gap-px px-4 py-0 sm:grid-cols-3 md:px-6">
            {L.stats.map((s, i) => (
              <div key={i} className="px-2 py-6 text-center sm:text-left" data-testid={`stat-${i}`}>
                <div className="font-heading text-2xl font-semibold text-terracotta md:text-3xl">{s.value}</div>
                <div className="mt-1 text-sm text-white/80">{s.label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* INTRO + BENEFITS */}
      <section className="mx-auto max-w-5xl px-4 py-14 md:px-6">
        <p className="max-w-3xl text-base leading-relaxed text-muted-foreground">{L.intro}</p>
        {podcastOn && (
          <Card className="mt-8 rounded-2xl border-terracotta/30 bg-[#FBF6F3] p-5" data-testid="landing-podcast">
            <div className="flex items-center gap-2 text-sm font-semibold text-terracotta-hover">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-terracotta/15"><Headphones className="h-4 w-4 text-terracotta-hover" /></span>
              Ascolta il podcast di presentazione
            </div>
            <p className="mt-1 text-sm text-muted-foreground">In pochi minuti ti spieghiamo a chi è rivolto e cosa copre.</p>
            <audio controls preload="none" className="mt-3 w-full" data-testid="landing-podcast-audio"
              src={`${API}/public/products/${code}/podcast-cliente/audio`} />
            <AiContentBadge variant="audio" className="mt-3" />
          </Card>
        )}
        <div className="mt-10 grid gap-5 sm:grid-cols-2">
          {L.benefits.map((b, i) => {
            const Icon = ICONS[b.icon] || ShieldCheck;
            return (
              <Card key={i} className="rounded-2xl border-border/60 p-6 shadow-sm transition-shadow hover:shadow-md" data-testid={`benefit-${i}`}>
                <div className="flex h-12 w-12 items-center justify-center rounded-xl bg-sage text-forest">
                  <Icon className="h-6 w-6" strokeWidth={1.75} />
                </div>
                <h3 className="mt-4 font-heading text-lg font-semibold text-forest">{b.title}</h3>
                <p className="mt-1.5 text-sm text-muted-foreground">{b.text}</p>
              </Card>
            );
          })}
        </div>
      </section>

      {/* COPERTURE */}
      {L.coperture?.length > 0 && (
        <section className="border-y border-border/60 bg-[#F0F4F1]">
          <div className="mx-auto max-w-5xl px-4 py-14 md:px-6">
            <h2 className="font-heading text-2xl font-semibold text-forest">Cosa comprende</h2>
            <p className="mt-2 max-w-2xl text-sm text-muted-foreground">
              Le principali aree di tutela previste. Le garanzie effettive e i relativi limiti sono definiti dalle condizioni di polizza.
            </p>
            <div className="mt-8 grid gap-3 sm:grid-cols-2">
              {L.coperture.map((c, i) => (
                <div key={i} className="flex items-start gap-3 rounded-xl border border-forest/10 bg-white p-4" data-testid={`copertura-${i}`}>
                  <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-terracotta" />
                  <span className="text-sm text-charcoal-800 text-forest/90">{c}</span>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* COME FUNZIONA */}
      <section className="mx-auto max-w-5xl px-4 py-14 md:px-6">
        <h2 className="font-heading text-2xl font-semibold text-forest">Come funziona</h2>
        <div className="mt-8 grid gap-5 sm:grid-cols-3">
          {COME_FUNZIONA.map((s, i) => (
            <div key={i} className="relative rounded-2xl border border-border/60 bg-white p-6" data-testid={`step-${i}`}>
              <div className="flex h-11 w-11 items-center justify-center rounded-xl bg-forest text-white">
                <s.icon className="h-5 w-5" strokeWidth={1.75} />
              </div>
              <h3 className="mt-4 font-heading text-base font-semibold text-forest">{s.title}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* DESTINATARI */}
      <section className="border-y border-border/60 bg-[#F0F4F1]">
        <div className="mx-auto max-w-5xl px-4 py-12 md:px-6">
          <h2 className="font-heading text-lg font-semibold text-forest">A chi è dedicato</h2>
          <div className="mt-4 flex flex-wrap gap-2.5">
            {L.destinatari.map((d) => (
              <span key={d} className="inline-flex items-center gap-1.5 rounded-full border border-forest/15 bg-white px-4 py-2 text-sm font-medium text-forest" data-testid="destinatario-chip">
                <CheckCircle2 className="h-4 w-4 text-terracotta" /> {d}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* ESEMPIO CONCRETO */}
      {L.casoConcreto && (
        <section className="mx-auto max-w-5xl px-4 py-14 md:px-6" data-testid="caso-concreto">
          <div className="overflow-hidden rounded-3xl border border-terracotta/20 bg-forest">
            <div className="grid gap-0 md:grid-cols-2">
              <div className="p-8 md:p-10">
                <span className="inline-flex items-center gap-1.5 rounded-full bg-terracotta/20 px-3 py-1 text-xs font-medium uppercase tracking-wide text-terracotta">
                  <Lightbulb className="h-3.5 w-3.5" /> Un esempio concreto
                </span>
                <h3 className="mt-4 flex items-center gap-2 font-heading text-lg font-semibold text-white/70">
                  <ShieldQuestion className="h-5 w-5 text-terracotta" /> La situazione
                </h3>
                <p className="mt-2 text-base leading-relaxed text-white/90">{L.casoConcreto.situazione}</p>
              </div>
              <div className="border-t border-white/10 bg-white/[0.04] p-8 backdrop-blur md:border-l md:border-t-0 md:p-10">
                <h3 className="flex items-center gap-2 font-heading text-lg font-semibold text-terracotta">
                  <CheckCircle2 className="h-5 w-5" /> Come interviene la polizza
                </h3>
                <p className="mt-2 text-base leading-relaxed text-white/90">{L.casoConcreto.soluzione}</p>
                <p className="mt-4 text-xs text-white/50">Esempio illustrativo e non vincolante. Garanzie, limiti ed esclusioni sono definiti dalle condizioni di polizza.</p>
              </div>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      {L.faq?.length > 0 && (
        <section className="mx-auto max-w-3xl px-4 py-14 md:px-6">
          <h2 className="font-heading text-2xl font-semibold text-forest">Domande frequenti</h2>
          <Accordion type="single" collapsible className="mt-6" data-testid="faq-accordion">
            {L.faq.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} data-testid={`faq-${i}`}>
                <AccordionTrigger className="text-left font-heading text-base text-forest hover:no-underline">
                  {f.q}
                </AccordionTrigger>
                <AccordionContent className="text-sm text-muted-foreground">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </section>
      )}

      {/* FORM */}
      <section ref={formRef} className="mx-auto max-w-3xl px-4 py-16 md:px-6">
        {sent ? (
          <Card className="rounded-2xl border-forest/20 p-10 text-center shadow-md" data-testid="landing-success">
            <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-full bg-sage text-forest">
              <Mail className="h-8 w-8" />
            </div>
            <h2 className="mt-5 font-heading text-2xl font-semibold text-forest">Richiesta inviata</h2>
            <p className="mt-2 text-muted-foreground">
              {sentMsg || "Grazie! Un nostro incaricato ti contatterà a breve, senza alcun impegno."}
            </p>
          </Card>
        ) : (
          <Card className="rounded-2xl border-border/60 p-6 shadow-md md:p-8">
            <h2 className="font-heading text-2xl font-semibold text-forest">Richiedi informazioni</h2>
            <p className="mt-1 text-sm text-muted-foreground">
              Lascia i tuoi dati: riceverai la documentazione informativa e verrai ricontattato. Il servizio è gratuito e senza impegno.
            </p>
            <form onSubmit={submit} className="mt-6 space-y-4">
              <div className="grid gap-4 sm:grid-cols-2">
                <div className="space-y-1.5">
                  <Label>Nome e cognome *</Label>
                  <Input data-testid="lead-nome" value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-1.5">
                  <Label>Ente / associazione (facoltativo)</Label>
                  <Input data-testid="lead-ente" value={form.ente} onChange={(e) => setForm({ ...form, ente: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-1.5">
                  <Label>Email *</Label>
                  <Input data-testid="lead-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-12" />
                </div>
                <div className="space-y-1.5">
                  <Label>Telefono (facoltativo)</Label>
                  <Input data-testid="lead-telefono" value={form.telefono} onChange={(e) => setForm({ ...form, telefono: e.target.value })} className="h-12" />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label>Tipo di richiesta</Label>
                <Select value={form.tipo_richiesta} onValueChange={(v) => setForm({ ...form, tipo_richiesta: v })}>
                  <SelectTrigger className="h-12" data-testid="lead-tipo"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {TIPI.map((t) => <SelectItem key={t.value} value={t.value} data-testid={`lead-tipo-${t.value}`}>{t.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-1.5">
                <Label>Note (facoltativo)</Label>
                <Textarea data-testid="lead-note" value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} rows={3} />
              </div>

              <div className="flex items-start gap-2 rounded-lg bg-[#F0F4F1] p-3 text-xs text-muted-foreground">
                <Info className="mt-0.5 h-4 w-4 shrink-0 text-forest" />
                <span>
                  Servizio meramente <b>informativo e non vincolante</b>: questa pagina non costituisce preventivo, offerta o proposta contrattuale. I dati saranno utilizzati esclusivamente per fornirti la documentazione richiesta ed essere ricontattato.
                </span>
              </div>

              {error && <p className="text-sm text-red-600" data-testid="lead-error">{error}</p>}
              <PrivacyConsent checked={privacyOk} onCheckedChange={setPrivacyOk} testid="lead-privacy-consent" />
              <Button type="submit" size="lg" disabled={loading || !privacyOk} className="w-full gap-2 rounded-full bg-terracotta hover:bg-terracotta/90" data-testid="lead-submit">
                {loading ? <Loader2 className="h-5 w-5 animate-spin" /> : <>Invia richiesta <ArrowRight className="h-5 w-5" /></>}
              </Button>
            </form>
          </Card>
        )}
      </section>

      <section className="mx-auto max-w-3xl px-4 pb-12 md:px-6">
        <ComplianceNotice />
      </section>

      <footer className="border-t border-border/60 py-8 text-center text-xs text-muted-foreground">
        <LegalFooterLinks className="mb-3" />
        Assietica · Portale Partner — {product.name}
      </footer>
    </div>
  );
}
